import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Plus, Loader2, Sparkles, Package, Image, X, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { formatCurrency, units } from "../../lib/utils";
import { IngredientFormatsSection } from "./IngredientFormatsSection";
import { PriceHistorySparkline } from "./PriceHistorySparkline";
import { SupplierSuggestionsDrawer } from "./SupplierSuggestionsDrawer";

export function IngredientDialog({
  open, onOpenChange, editingIngredient, formData, setFormData,
  onSubmit, saving, suppliers, closeDialog, restaurantId,
}) {
  const [estimatingWaste, setEstimatingWaste] = useState(false);
  const [searchingImage, setSearchingImage] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [imageOptions, setImageOptions] = useState([]);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [supplierDrawerOpen, setSupplierDrawerOpen] = useState(false);

  const estimateWasteWithAI = async () => {
    if (!formData.name.trim()) { toast.error("Introduce el nombre primero"); return; }
    setEstimatingWaste(true);
    try {
      const result = await api.aiEstimateWaste(formData.name, "", restaurantId);
      setFormData(prev => ({ ...prev, waste_percentage: result.waste_percentage.toString() }));
      toast.success(`Merma estimada: ${result.waste_percentage}%`);
      if (result.explanation) toast.info(result.explanation, { duration: 5000 });
    } catch { toast.error("Error al estimar merma"); }
    finally { setEstimatingWaste(false); }
  };

  const searchIngredientImage = async () => {
    if (!formData.name.trim()) { toast.error("Introduce el nombre primero"); return; }
    setSearchingImage(true); setShowImagePicker(true); setGeneratingImage(false);
    try {
      const result = await api.searchStockImages(`${formData.name} food ingredient`, 6);
      setImageOptions(result.images || []);
    } catch { toast.error("Error al buscar imagenes"); setImageOptions([]); }
    finally { setSearchingImage(false); }
  };

  const generateIngredientImage = async () => {
    if (!formData.name.trim()) { toast.error("Introduce el nombre primero"); return; }
    setGeneratingImage(true); setShowImagePicker(false); setImageOptions([]);
    try {
      const result = await api.generateIngredientImage(formData.name, restaurantId);
      if (result.image_url) { setFormData(prev => ({ ...prev, image_url: result.image_url })); toast.success("Imagen generada con IA"); }
      else toast.error("No se pudo generar la imagen");
    } catch (error) { toast.error(error.message || "Error al generar imagen con IA"); }
    finally { setGeneratingImage(false); }
  };

  const handleClose = () => {
    setImageOptions([]); setShowImagePicker(false); setGeneratingImage(false);
    closeDialog();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); else onOpenChange(true); }}>
      <DialogTrigger asChild>
        <Button className="rounded-full" data-testid="add-ingredient-btn">
          <Plus className="w-4 h-4 mr-2" /> Ingrediente
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingIngredient ? "Editar ingrediente" : "Nuevo ingrediente"}</DialogTitle>
          <DialogDescription>{editingIngredient ? "Modifica los datos del ingrediente" : "Añade un nuevo ingrediente al inventario"}</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Nombre *</Label>
            <Input placeholder="Ej: Tomate, Aceite de oliva..." value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })} data-testid="ingredient-name-input" />
          </div>

          {/* Image section */}
          <div className="space-y-3">
            <Label>Imagen del producto</Label>
            <div className="flex items-start gap-3">
              <div className="w-20 h-20 rounded-xl border-2 border-dashed border-border flex items-center justify-center flex-shrink-0 overflow-hidden bg-muted/30">
                {formData.image_url ? (
                  <img src={formData.image_url} alt={formData.name || "Ingrediente"} className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = "none"; e.target.parentElement.classList.add("flex"); }} />
                ) : ( <Package className="w-8 h-8 text-muted-foreground" /> )}
              </div>
              <div className="flex-1 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={searchIngredientImage}
                    disabled={searchingImage || generatingImage || !formData.name.trim()} className="h-9 text-xs gap-1.5"
                    data-testid="search-ingredient-image-btn">
                    {searchingImage ? <Loader2 className="w-3 h-3 animate-spin" /> : <Image className="w-3 h-3" />}
                    {searchingImage ? "Buscando..." : "Buscar foto"}
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={generateIngredientImage}
                    disabled={generatingImage || searchingImage || !formData.name.trim()}
                    className="h-9 text-xs gap-1.5 border-primary/40 text-primary hover:bg-primary/5"
                    data-testid="generate-ingredient-image-btn">
                    {generatingImage ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    {generatingImage ? "Generando..." : "Generar con IA"}
                  </Button>
                </div>
                {formData.image_url && (
                  <Button type="button" variant="ghost" size="sm" className="h-7 text-xs text-destructive w-full"
                    onClick={() => { setFormData({ ...formData, image_url: "" }); setShowImagePicker(false); }}>
                    <X className="w-3 h-3 mr-1" /> Quitar imagen
                  </Button>
                )}
                {!formData.name.trim() && <p className="text-xs text-muted-foreground">Escribe el nombre del ingrediente para activar las opciones de imagen.</p>}
              </div>
            </div>
            {showImagePicker && (
              <div className="border rounded-lg p-2">
                {searchingImage ? (
                  <div className="flex items-center justify-center h-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
                ) : imageOptions.length > 0 ? (
                  <>
                    <p className="text-xs text-muted-foreground mb-2">Haz clic en una foto para seleccionarla:</p>
                    <div className="grid grid-cols-3 gap-1.5">
                      {imageOptions.map((img, idx) => (
                        <button key={idx} type="button" onClick={() => { setFormData({ ...formData, image_url: img.thumbnail || img.url }); setShowImagePicker(false); }}
                          className={`relative rounded-md overflow-hidden border-2 transition-all hover:border-primary ${formData.image_url === (img.thumbnail || img.url) ? "border-primary" : "border-transparent"}`}
                          data-testid={`image-option-${idx}`}>
                          <img src={img.thumbnail || img.url} alt={`Opcion ${idx + 1}`} className="w-full h-16 object-cover"
                            onError={(e) => { e.target.parentElement.style.display = "none"; }} />
                        </button>
                      ))}
                    </div>
                  </>
                ) : <p className="text-center text-xs text-muted-foreground py-4">No se encontraron imagenes. Prueba a generar una con IA.</p>}
              </div>
            )}
            {generatingImage && (
              <div className="border rounded-lg p-4 flex items-center justify-center gap-3 bg-primary/5 border-primary/20">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
                <div>
                  <p className="text-sm font-medium text-primary">Generando imagen con IA...</p>
                  <p className="text-xs text-muted-foreground">Esto puede tardar unos segundos</p>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Unidad</Label>
              <Select value={formData.unit} onValueChange={(v) => setFormData({ ...formData, unit: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{units.map((unit) => <SelectItem key={unit.value} value={unit.value}>{unit.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Coste por unidad (EUR) *</Label>
              <Input type="number" step="0.01" placeholder="0.00" value={formData.cost_per_unit}
                onChange={(e) => setFormData({ ...formData, cost_per_unit: e.target.value })} data-testid="ingredient-cost-input" />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="flex items-center justify-between">
              <span className="flex items-center gap-2">% Merma / Desperdicio</span>
              <Button type="button" variant="ghost" size="sm" onClick={estimateWasteWithAI} disabled={estimatingWaste || !formData.name.trim()} className="h-7 text-xs">
                {estimatingWaste ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />} Calcular con IA
              </Button>
            </Label>
            <Input type="number" step="0.1" min="0" max="100" placeholder="0" value={formData.waste_percentage}
              onChange={(e) => setFormData({ ...formData, waste_percentage: e.target.value })} data-testid="ingredient-waste-input" />
            <p className="text-xs text-muted-foreground">
              Coste efectivo: {formatCurrency(parseFloat(formData.cost_per_unit || 0) * (1 + parseFloat(formData.waste_percentage || 0) / 100))} / {formData.unit}
            </p>
          </div>

          {editingIngredient && restaurantId && (
            <div className="rounded-lg border border-border/60 bg-muted/20 p-3">
              <PriceHistorySparkline
                restaurantId={restaurantId}
                ingredientId={editingIngredient.ingredient_id}
                days={90}
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Stock actual</Label>
              <Input type="number" step="0.01" placeholder="0" value={formData.current_stock}
                onChange={(e) => setFormData({ ...formData, current_stock: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Stock minimo</Label>
              <Input type="number" step="0.01" placeholder="0" value={formData.min_stock}
                onChange={(e) => setFormData({ ...formData, min_stock: e.target.value })} />
            </div>
          </div>

          {suppliers.length > 0 && (
            <div className="space-y-2">
              <Label>Proveedor principal</Label>
              <Select value={formData.supplier_id} onValueChange={(v) => setFormData({ ...formData, supplier_id: v })}>
                <SelectTrigger><SelectValue placeholder="Seleccionar proveedor..." /></SelectTrigger>
                <SelectContent>{suppliers.map((s) => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}

          {editingIngredient && (
            <IngredientFormatsSection
              ingredientId={editingIngredient.ingredient_id}
              unit={editingIngredient.unit}
              costPerUnit={formData.cost_per_unit}
            />
          )}

          <div className="flex gap-3 justify-end items-center flex-wrap">
            {restaurantId && formData.name?.trim() && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setSupplierDrawerOpen(true)}
                className="mr-auto gap-1.5 text-violet-700 dark:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-950/30"
                data-testid="ingredient-suggest-suppliers-btn"
              >
                <Sparkles className="w-3.5 h-3.5" />
                Sugerir proveedores
              </Button>
            )}
            <Button type="button" variant="outline" onClick={handleClose}>Cancelar</Button>
            <Button type="submit" disabled={saving} data-testid="save-ingredient-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingIngredient ? "Guardar" : "Crear")}
            </Button>
          </div>
        </form>
      </DialogContent>

      {restaurantId && (
        <SupplierSuggestionsDrawer
          open={supplierDrawerOpen}
          onOpenChange={setSupplierDrawerOpen}
          ingredientName={formData.name}
          restaurantId={restaurantId}
        />
      )}
    </Dialog>
  );
}
